import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@hostingbv/lib/src/components/form/Button";
import { PageHeader } from "@hostingbv/lib/src/components/content/PageHeader";
import { ContentContainer } from "@hostingbv/lib/src/components/content/ContentContainer";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { config } from "../config";
import { Breadcrumbs } from "@hostingbv/lib/src/components/content/Breadcrumbs";
import { SectionHeader } from "@hostingbv/lib/src/components/content/SectionHeader";
import { Input } from "@hostingbv/lib/src/components/form/Input";
import { useApiClient } from "@hostingbv/lib/src/api";
import { useFetchedResource } from "@hostingbv/lib/src/util/useFetchedResource";
import { useRefresh, useRefreshEffect } from "@hostingbv/lib/src/components/RefreshController";
import { Server } from "@hostingbv/lib/src/api/partner/models/Server";
import { Callout } from "@hostingbv/lib/src/components/content/Callout";
import { SectionDivider } from "@hostingbv/lib/src/components/content/SectionDivider";
import { useModal } from "@hostingbv/lib/src/components/layout/ModalProvider";
import { Autocomplete } from "@hostingbv/lib/src/components/form/Autocomplete";
import { AddDeploymentKeyModal } from "../modals/AddDeploymentKeyModal";
import { useRealtimeDeployments } from "../components/deployments/RealtimeDeployments";
import { PageIconHeader } from "@hostingbv/lib/src/components/PageIconHeader";
import { faAdd, faCheck, faKey, faRocket, faShop } from "@fortawesome/free-solid-svg-icons";
import { Customer } from "@hostingbv/lib/src/api/common/models/Customer";

export const SiteCreate: FC = () => {
  const navigate = useNavigate()
  const { partnerClient } = useApiClient(config)
  const { awaitNextDeploymentFinished } = useRealtimeDeployments()!
  const refresh = useRefresh()
  const [host, setHost] = useState('')
  const [customer, setCustomer] = useState('')
  const customerOptions = useCustomerOptions()
  useEffect(() => {
    if (customer === "" && Object.keys(customerOptions).length > 0) {
      setCustomer(Object.keys(customerOptions)[0])
    }
  }, [customerOptions, customer])
  const [server, setServer] = useState('')
  const { servers, serverOptions } = useServerOptions()
  useEffect(() => {
    if (server === "" && Object.keys(serverOptions).length > 0) {
      setServer(Object.keys(serverOptions)[0])
    }
  }, [serverOptions, server])
  const { personalKeys, selectKey, deploymentKeys, toggleSelectKey, selectedKeys } = useSshKeys()
  const isSubmissionValid = host.length > 3 && customer !== "" && server !== ""
  const [error, setError] = useState<string | null>(null)
  const submit = async () => {
    setError(null)
    const type = servers?.find((s) => s.id === server)?.serverType === 'shared_hosting' ? 'shared_hosting_subscription' : 'windhosting_site';
    const response = await partnerClient.createSite(host, type, customer, server, selectedKeys)

    if (!response.success) {
      setError(response.error)
      return
    }

    await awaitNextDeploymentFinished(response.data.entityId!)
    refresh()
    navigate(`/sites`)
  }

  const { open: openAddDeploymentKeyModal } = useModal({ title: 'Add new SSH key', body: <AddDeploymentKeyModal filterKeys={(k) => k.type === 'deployment'} onKeySelected={(depKey) => selectKey(depKey.id)} /> })

  return (
    <ContentContainer>
      <PageIconHeader icon={faShop}>
        <Breadcrumbs crumbs={[
          { label: "Sites", href: "/sites" },
        ]} currentPage={"Deploy new site"} />
        <PageHeader>Deploy new site</PageHeader>
      </PageIconHeader>

      {error !== null ? <Callout color={"danger"}>{error}</Callout> : <></>}

      <SectionHeader>Site properties</SectionHeader>
      <section className={"space-y-4 mb-8"}>
        <Input type={"text"} label={"Address"} value={host} onChange={setHost} placeholder={"blog.example.com"} />
        <Autocomplete label={"Customer"} value={customer} onChange={setCustomer} options={customerOptions} />
        <Autocomplete label={"Server"} value={server} onChange={setServer} options={serverOptions} />
      </section>
      <SectionDivider />
      <SectionHeader>SSH Access</SectionHeader>
      <ul className={"flex flex-wrap gap-4 mb-6"}>
        {personalKeys.map(key => {
          const selected = selectedKeys.includes(key.id)
          return <li key={key.id}>
            <button onClick={toggleSelectKey(key.id)} className={`relative flex justify-start items-center h-12 w-64 text-sm rounded-lg font-medium px-4 py-3 border ${selected ? 'bg-brand-100 text-brand-900 border-brand-500' : 'bg-slate-100 hover:bg-slate-200 cursor-pointer text-slate-900 border-transparent'}`}>
              <FontAwesomeIcon icon={faKey} className={"mr-2"} />
              {key.displayName}
              {selected && <div className={"h-5 w-5 absolute -top-2 -left-2 rounded-full bg-brand-600 text-white flex items-center justify-center"}>
                <FontAwesomeIcon icon={faCheck} className={"h-3 w-3"} />
              </div>}
            </button>
          </li>
        })}
        {deploymentKeys.filter(k => selectedKeys.includes(k.id)).map(key => {
          const selected = selectedKeys.includes(key.id)
          return <li key={key.id}>
            <button onClick={toggleSelectKey(key.id)} className={`relative flex justify-start items-center h-12 w-64 text-sm rounded-lg font-medium px-4 py-3 border ${selected ? 'bg-brand-100 text-brand-900 border-brand-500' : 'bg-slate-100 hover:bg-slate-200 cursor-pointer text-slate-900 border-transparent'}`}>
              <FontAwesomeIcon icon={faKey} className={"mr-2"} />
              {key.displayName}
              {selected && <div className={"h-5 w-5 absolute -top-2 -left-2 rounded-full bg-brand-600 text-white flex items-center justify-center"}>
                <FontAwesomeIcon icon={faCheck} className={"h-3 w-3"} />
              </div>}
            </button>
          </li>
        })}
      </ul>
      <Button type={"secondary"} size={"sm"} text={"Add deployment key"} icon={faAdd} onClick={() => {
        openAddDeploymentKeyModal()
      }} />

      <SectionDivider />
      <Button type={'primary'} size={'sm'} disabled={!isSubmissionValid} onClick={submit} icon={faRocket} text={'Deploy site'} />
    </ContentContainer>
  );
}

function useCustomerOptions() {
  const { partnerClient } = useApiClient(config)
  const { resource: customerResource, reload: reloadCustomers } = useFetchedResource(() => partnerClient.getAllCustomers())
  const customers: Customer[] | null = useMemo(() => customerResource?.success === true ? customerResource.data : null, [customerResource])
  useRefreshEffect(() => { reloadCustomers() })
  return useMemo(() => {
    return Object.fromEntries(customers?.map(customer => [customer.id, customer.displayName]) ?? [])
  }, [customers])
}

function useServerOptions() {
  const { partnerClient } = useApiClient(config)
  const { resource, reload } = useFetchedResource(() => partnerClient.getAllServers())
  const servers: Server[] | null = useMemo(() => resource?.success === true ? resource.data : null, [resource])
  useRefreshEffect(() => { reload() })
  return {
    servers,
    serverOptions: useMemo(() => {
      return Object.fromEntries(
        servers?.filter(s => ['shared_hosting', 'windhosting_server'].includes(s.serverType)).map(server => [server.id, server.displayName]) ?? []
      )
    }, [servers])
  }
}

function useSshKeys() {
  const { partnerClient } = useApiClient(config)
  const { resource, reload } = useFetchedResource(() => partnerClient.getAllSshKeys())
  useRefreshEffect(() => { reload() })
  const keys = useMemo(() => {
    return {
      personalKeys: resource?.success === true ? resource.data.filter(k => k.type === "personal") : [],
      deploymentKeys: resource?.success === true ? resource.data.filter(k => k.type === "deployment") : [],
    }
  }, [resource])
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  return {
    ...keys,
    selectKey: (id: string) => {
      setSelectedKeys(old => [...old, id])
    },
    toggleSelectKey: useCallback((id: string) => () => {
      setSelectedKeys(oldSelectedKeys => {
        if (oldSelectedKeys.includes(id)) {
          return oldSelectedKeys.filter(k => k !== id)
        } else {
          return [...oldSelectedKeys, id]
        }
      })
    }, []),
    selectedKeys,
  }
}
